import { BaseModelWithFiles } from "@/models/base-with-files.model"
import {
  type ITransaction,
  SurchargeTransactionTypes
} from "@/interfaces/api/transaction.interface"
import { type Moment } from "moment"
import { Project } from "@/models/project.model"
import { ModelFactory } from "@/factories/model-factory"
import type { TransactionItem } from "@/types/models/administrative-events.ts"

export abstract class Transaction extends BaseModelWithFiles implements ITransaction {
  public static className = "Transaction"
  public static verboseName = "Transactie"

  public static managedResolveFields = ["project"]

  instruction: string

  project: Project

  date: Moment

  isSurchargeFor: string | undefined

  quantity: string | undefined

  unitPrice: string | undefined

  sortIndex: number

  hasCustomUnitPrice: boolean

  comment: string | undefined

  surchargeType: SurchargeTransactionTypes | undefined

  // Annotated
  instructionReference: string
  instructionDate?: Moment
  orderReference: string
  projectExecutorFullName: string

  surchargeDescription: string | undefined

  surchargePercent: string | undefined

  surchargePerUnit: string | undefined

  surchargeFlat: string | undefined

  protected constructor(transaction: ITransaction, managed = false) {
    super(transaction, managed)

    this.project = ModelFactory.childModel(Project, transaction.project, managed)
    this.instruction = transaction.instruction
    this.isSurchargeFor = transaction.isSurchargeFor

    this.date = transaction.date
    this.quantity = transaction.quantity
    this.unitPrice = transaction.unitPrice
    this.sortIndex = transaction.sortIndex
    this.hasCustomUnitPrice = transaction.hasCustomUnitPrice
    this.comment = transaction.comment
    this.surchargeType = transaction.surchargeType

    this.projectExecutorFullName = transaction.projectExecutorFullName
    this.instructionReference = transaction.instructionReference
    this.orderReference = transaction.orderReference
    this.instructionDate = transaction.instructionDate
    this.surchargeDescription = transaction.surchargeDescription
    this.surchargePercent = transaction.surchargePercent
    this.surchargeFlat = transaction.surchargeFlat
    this.surchargePerUnit = transaction.surchargePerUnit
  }

  public abstract getItem(): TransactionItem

  public getTotalAmount(): number {
    if (!this.quantity || !this.unitPrice) {
      return 0
    }
    return parseFloat(this.quantity) * parseFloat(this.unitPrice)
  }

  public isComplete(): boolean {
    return (
      !!this.project &&
      !!this.date &&
      !!this.quantity &&
      parseFloat(this.quantity) !== 0 &&
      !!this.unitPrice
    )
  }

  public getSurchargePercentage() {
    if (!this.isSurchargeFor) {
      throw new Error(
        "Transaction is not a surcharge, so it makes no sense to get a surcharge percentage"
      )
    }
    if (!this.surchargePercent) {
      throw new Error("No surcharge percentage defined")
    }
    return parseFloat(this.surchargePercent)
  }
}
